/**
 * Списание техники — с ПРИЧИНОЙ (AK-764, Р22).
 *
 * Раньше «Списать» уводило машину из парка одним кликом и без единого слова о
 * том, куда она делась. Через месяц по такой записи не отличить проданный
 * комбайн от заведённого дублем — а именно этим различием потом объясняют
 * недостачу техники под план.
 *
 * Форма — 1:1 с архивом полей (`ArchiveFieldsDialog`, AK-746): код из списка +
 * необязательный комментарий. Один приём на два похожих действия, чтобы
 * человек не переучивался на второй вкладке той же карточки.
 */
import { useEffect, useState } from 'react';
import { Archive, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { machineryPlural } from './machineryLogic';
import {
  WRITE_OFF_REASON_CODES,
  WRITE_OFF_REASON_LABELS,
  parseWriteOffReason,
  type WriteOffReasonCode,
} from './writeOffReason';

export function WriteOffMachineryDialog({
  open,
  count,
  pending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  count: number;
  pending: boolean;
  onCancel: () => void;
  /** Причина уже собрана в строку «код: комментарий». */
  onConfirm: (reason: string) => void;
}) {
  const [code, setCode] = useState<WriteOffReasonCode | null>(null);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Новый вызов — новый разговор: причина от прошлой пачки к этой не относится.
  useEffect(() => {
    if (open) {
      setCode(null);
      setComment('');
      setError(null);
    }
  }, [open]);

  const submit = () => {
    const parsed = parseWriteOffReason(code, comment);
    if (!parsed.ok) {
      // Набранное НЕ затираем: человек видит, что написал, и правит.
      setError(parsed.error);
      return;
    }
    setError(null);
    onConfirm(parsed.value);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Списать {machineryPlural(count)}?</DialogTitle>
          <DialogDescription>
            Машина уйдёт из парка, но останется в истории: чем сделаны прошлые
            работы, видно будет по-прежнему. Действие обратимо — списанные
            показываются галочкой «Показывать списанные» и возвращаются кнопкой
            «Вернуть в парк».
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="machinery-writeoff-reason">Причина списания</Label>
            <Select
              value={code ?? undefined}
              onValueChange={(v) => {
                setCode(v as WriteOffReasonCode);
                // Правка снимает ошибку — иначе человек заперт в красном поле.
                if (error) setError(null);
              }}
            >
              <SelectTrigger
                id="machinery-writeoff-reason"
                data-testid="machinery-writeoff-reason"
                aria-invalid={error ? true : undefined}
                className={cn(error && 'border-destructive focus:ring-destructive')}
              >
                <SelectValue placeholder="Выберите причину" />
              </SelectTrigger>
              <SelectContent>
                {WRITE_OFF_REASON_CODES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {WRITE_OFF_REASON_LABELS[c]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {error ? (
              <span role="alert" className="text-xs text-destructive">
                {error}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">
                По ней потом видно, куда делась машина, — и по ней же читается
                выбытие парка за сезон.
              </span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="machinery-writeoff-comment">Комментарий (необязательно)</Label>
            <Input
              id="machinery-writeoff-comment"
              data-testid="machinery-writeoff-comment"
              placeholder="например: продана соседнему хозяйству 20.08"
              value={comment}
              disabled={pending}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
              }}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            Отмена
          </Button>
          <Button
            variant="destructive"
            data-testid="machinery-writeoff-confirm"
            onClick={submit}
            disabled={pending}
          >
            {pending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Списываю…
              </>
            ) : (
              <>
                <Archive className="mr-2 h-4 w-4" />
                Списать
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
